import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { PatientNameSearchResultModel, PatientSearchStateEvent } from '../../../types/patient-search-type';
import { CommonUtilsService } from 'src/app/features/shared/utils/common-utils.service';
import { DebounceService } from 'src/app/features/shared/services/debounce-service';
import { clearTestPatientResultState, closeErrorAlert, invokedGetTestPatientAPI } from '../../../store/actions/patient-search.action';
import { selectPatientSearchStateEvent, selectTestPatientResult } from '../../../store/selectors/patient-search.selector';
import { AlertService } from 'src/app/features/shared/utils/alert.service';

@Component({
  selector: 'app-patient-search',
  template: `
    <app-form-control-selectbox 
      label="Patient Name" 
      [required]="false" 
      [items]="patientNameSearchResult" 
      placeholder="Search Last Name, First Name" 
      bindValue="patientId"
      bindLabel="patientName"
      [isLoading] = "isLoading"
      [searchFn]="customSearchFn"
      (searchEvent)="onPatientSearch($event)" 
      (clearEvent)="onClearPatient()"
      [ngModel]="selectedPatientId"
      (ngModelChange)="onChangeEvent($event)">
    </app-form-control-selectbox>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PatientSearchComponent),
      multi: true
    }
  ]
})
export class PatientSearchComponent implements OnInit, OnDestroy, ControlValueAccessor {
  isLoading: boolean = false;

  @Input() selectedPatientId: number | null = null;
  @Input() patientName: string = '';
  @Output() selectedPatient = new EventEmitter<number | null>();

  patientNameSearchResult: PatientNameSearchResultModel[] = [];
  private subscriptions$ = new Subscription();
  isLoading$ = this.store.select(selectPatientSearchStateEvent);
  private selectPatients$ = this.store.select(selectTestPatientResult);

  onChange: (value: number | null) => void = () => { };
  onTouched: () => void = () => { };

  constructor(
    private store: Store,
    private commonUtilsService: CommonUtilsService,
    private debounceService: DebounceService,
    private alert: AlertService
  ) { }

  ngOnInit(): void {
    this.subscriptions$.add(this.selectPatients$.subscribe((result: PatientNameSearchResultModel[] | null) => this.bindPatientResults(result)));
    this.subscriptions$.add(this.isLoading$.subscribe(async (data: PatientSearchStateEvent) => this.handlePatientSearchStateEvent(data)));
    this.fetchPatientResults(this.patientName);
  }

  onPatientSearch(patientName: string): void {
    this.debounceService.debounce('getPatientsSuggestion', () => {
      this.fetchPatientResults(patientName);
    });
  }

  onClearPatient(): void {
    this.selectedPatientId = null;
    this.onChange(null);
    this.fetchPatientResults('');
  }

  onChangeEvent(value: number | null): void {
    if (this.selectedPatientId !== value) {
      this.selectedPatientId = value;
      this.selectedPatient.emit(value);
      this.onChange(value);
    }
  }

  writeValue(value: number | null): void {
    this.selectedPatientId = value;
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = (value) => {
      if (this.selectedPatientId !== value) {
        this.selectedPatientId = value;
        this.selectedPatient.emit(value);
        fn(value);
      }
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private fetchPatientResults(patient: string): void {
    const { lastName, firstName } = this.commonUtilsService.splitFullName(patient);
    const filterValue = {
      lastName: this.commonUtilsService.trimAndLowerCase(lastName),
      firstName: this.commonUtilsService.trimAndLowerCase(firstName),
    };
    this.store.dispatch(invokedGetTestPatientAPI(filterValue));
  }

  bindPatientResults(patients: PatientNameSearchResultModel[] | null): void {
    if (patients) {
      this.patientNameSearchResult = patients.map(patient => ({
        ...patient,
        patientName: this.normalizeString(patient.patientName),
      }));
    } else {
      this.patientNameSearchResult = [];
    }
  }

  async handlePatientSearchStateEvent(data: PatientSearchStateEvent) {
    this.isLoading = data.testPatientLoading ?? false;
    if (data.showErrorAlert) {
      const response = await this.alert.failureAlert(data.errorMessage);
      if (response.isConfirmed)
        this.store.dispatch(closeErrorAlert({}));
    }
  }

  customSearchFn = (term: string, item: any): boolean => {
    const normalizedTerm = this.normalizeString(term);
    const normalizedName = this.normalizeString(item.patientName);
    const { lastName: searchLastName, firstName: searchFirstName } = this.commonUtilsService.splitFullName(normalizedTerm);
    const { lastName, firstName } = this.commonUtilsService.splitFullName(normalizedName);
    const lastNameMatches = lastName.startsWith(searchLastName);
    const firstNameMatches = !searchFirstName || firstName.startsWith(searchFirstName);

    return lastNameMatches && firstNameMatches;
  };

  private normalizeString(value: string): string {
    return value?.trim().replace(/\s*,\s*/g, ', ').toLowerCase() || '';
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
    this.store.dispatch(clearTestPatientResultState());
  }
}